import { RefObject, useEffect, useRef } from "react"
import FullCalendar from "@fullcalendar/react"

import { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"

import { SLOT_DURATION, SLOT_SIZE } from "./helpers"

type Options = {
    thresholdPx?: number
    debounceMs?: number
    cooldownMs?: number
    shiftByDays?: number // default 1
    edgeBufferHours?: number // default 3 (how far from edge we leave the scroll position after loading the next/prev day)
}

type ScrollMomentum = Readonly<{
    prevLeft: number
    netInWindow: number
}>

export function useAutoEdgeTimelineNavigation(
    dateTimeFactory: DateTimeFactory,
    calendarRef: RefObject<FullCalendar | null>,
    containerRef: RefObject<HTMLElement | null>,
    isEnabled: boolean,
    opts?: Options,
) {
    const primaryScrollerRef = useRef<HTMLElement | null>(null)
    const scrollersRef = useRef<HTMLElement[]>([])
    const debounceTimerRef = useRef<number | null>(null)
    const scrollMomentumRef = useRef<ScrollMomentum>({ prevLeft: 0, netInWindow: 0 })

    const coolingRef = useRef(false)
    const coolingTimerRef = useRef<number | null>(null)
    const attachTimerRef = useRef<number | null>(null)
    const observerRef = useRef<MutationObserver | null>(null)

    useEffect(() => {
        if (!isEnabled || !calendarRef.current || !containerRef.current) return

        const threshold = opts?.thresholdPx ?? 2 * SLOT_SIZE // Threshold at the edge: ~2 grid cells → less sensitive to small tremors, tied to UI size
        const debounceMs = opts?.debounceMs ?? 60 // debounce to filter out micro-scroll movements without noticeable delay
        const cooldownMs = opts?.cooldownMs ?? 450 // Cooldown after auto-refresh: allow FullCalendar to redraw and remove scroll inertia
        const shiftByDays = opts?.shiftByDays ?? 1
        const edgeBufferHours = opts?.edgeBufferHours ?? 3

        const api = calendarRef.current.getApi()
        const rootEl = containerRef.current

        const clearCoolingTimer = () => {
            if (coolingTimerRef.current) {
                window.clearTimeout(coolingTimerRef.current)
                coolingTimerRef.current = null
            }
        }
        const setCooling = () => {
            coolingRef.current = true
            clearCoolingTimer()
            coolingTimerRef.current = window.setTimeout(() => {
                coolingRef.current = false
                coolingTimerRef.current = null
            }, cooldownMs)
        }

        const getPixelsPerDay = () => {
            /**
             * IMPORTANT:
             * We must not assume SLOT_SIZE == actual rendered slot width.
             * FullCalendar can compress/expand slots depending on container width, fonts, etc.
             * If we overshoot, scrollLeft clamps to 0 and it looks like a jump to the far left.
             *
             * So we derive px/day from the *actual* scrollable width and the view duration.
             */
            const el = primaryScrollerRef.current
            if (!el) {
                const pxPerHour = (60 / SLOT_DURATION) * SLOT_SIZE
                return pxPerHour * 24
            }

            const start = dateTimeFactory.fromJSDate(api.view.currentStart).startOf("day")
            const end = dateTimeFactory.fromJSDate(api.view.currentEnd).startOf("day")
            const daysInView = Math.max(1, Math.round(end.diff(start, "days").days))

            const pxPerDay = el.scrollWidth / daysInView
            if (!Number.isFinite(pxPerDay) || pxPerDay <= 0) {
                const pxPerHour = (60 / SLOT_DURATION) * SLOT_SIZE
                return pxPerHour * 24
            }
            return pxPerDay
        }

        const clampScrollLeft = (el: HTMLElement, value: number) => {
            const max = Math.max(0, el.scrollWidth - el.clientWidth)
            return Math.max(0, Math.min(max, value))
        }

        /**
         * FullCalendar doesn't reliably apply `duration` updates at runtime for custom views.
         * Instead, we slide the 3-day window by N days and compensate scrollLeft so the user
         * remains on the same absolute time (no visible jump), while new days become available
         * to scroll into.
         */
        const shiftWindowAndCompensate = (dir: 1 | -1) => {
            if (!primaryScrollerRef.current) return

            const prevPrimary = primaryScrollerRef.current
            const prevScrollLeft = prevPrimary.scrollLeft
            const dayPx = getPixelsPerDay()
            const bufferPx = Math.max(0, Math.min(dayPx, (dayPx / 24) * edgeBufferHours))
            const effectiveDayShiftPx = shiftByDays * Math.max(0, dayPx - bufferPx)

            setCooling()
            scrollMomentumRef.current = {
                prevLeft: prevPrimary.scrollLeft,
                netInWindow: 0,
            }

            const newStart = dateTimeFactory.fromJSDate(api.view.currentStart).plus({ days: dir * shiftByDays })
            api.gotoDate(newStart.toJSDate())

            const applyScrollCompensation = () => {
                // Re-attach in case FullCalendar recreated the scroller DOM during navigation.
                // skipCooling: attach() runs up to maxAttempts times via rAF; setCooling() would reset
                // the cooldown timer each frame (~450ms + N frames instead of ~450ms).
                attach({ skipCooling: true })
                const primary = primaryScrollerRef.current
                const allScrollers = scrollersRef.current
                if (!primary || allScrollers.length === 0) return

                const desired = prevScrollLeft - dir * effectiveDayShiftPx
                const clampedForPrimary = clampScrollLeft(primary, desired)

                // Apply to all horizontal scrollers to avoid header/body drift artifacts.
                for (const el of allScrollers) {
                    el.scrollLeft = clampScrollLeft(el, clampedForPrimary)
                }
            }

            // Let FullCalendar update DOM after gotoDate, then compensate (retry a few frames).
            let attempts = 0
            const maxAttempts = 8
            const tick = () => {
                applyScrollCompensation()
                attempts += 1
                if (attempts < maxAttempts) {
                    window.requestAnimationFrame(tick)
                }
            }
            window.requestAnimationFrame(() => window.requestAnimationFrame(tick))

            // keep previous behavior: set a short cooldown so we don't trigger repeatedly during inertia
        }

        const onScroll = () => {
            if (!primaryScrollerRef.current || coolingRef.current) return
            const el = primaryScrollerRef.current
            const m = scrollMomentumRef.current
            const delta = el.scrollLeft - m.prevLeft
            scrollMomentumRef.current = {
                prevLeft: el.scrollLeft,
                netInWindow: m.netInWindow + delta,
            }

            if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = window.setTimeout(() => {
                const el = primaryScrollerRef.current!
                const { scrollLeft, clientWidth, scrollWidth } = el
                const atStart = scrollLeft <= threshold
                const atEnd = scrollLeft + clientWidth >= scrollWidth - threshold

                const net = scrollMomentumRef.current.netInWindow
                scrollMomentumRef.current = {
                    prevLeft: el.scrollLeft,
                    netInWindow: 0,
                }

                // Net movement in the debounce window: avoids missing a transition when the last
                // micro-delta is opposite to the scroll that reached the edge (bounce/inertia).
                if (net > 0 && atEnd) {
                    shiftWindowAndCompensate(1)
                    return
                }
                if (net < 0 && atStart) {
                    shiftWindowAndCompensate(-1)
                    return
                }
            }, debounceMs)
        }

        const detach = () => {
            if (primaryScrollerRef.current) {
                primaryScrollerRef.current.removeEventListener("scroll", onScroll)
            }
            if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current)

            primaryScrollerRef.current = null
            scrollersRef.current = []
        }

        const pickTimelineScrollers = () => {
            const candidates = Array.from(rootEl.querySelectorAll<HTMLElement>(".fc-scroller"))
            if (!candidates.length) return null
            const horizontal = candidates.filter((el) => el.scrollWidth > el.clientWidth + 8)
            if (!horizontal.length) return null

            let best: HTMLElement | null = null
            let bestWidth = 0
            for (const el of horizontal) {
                if (el.scrollWidth > bestWidth) {
                    best = el
                    bestWidth = el.scrollWidth
                }
            }
            return { primary: best, all: horizontal }
        }

        const attach = (options?: Readonly<{ skipCooling?: boolean }>) => {
            detach()
            const picked = pickTimelineScrollers()
            if (!picked?.primary) return
            primaryScrollerRef.current = picked.primary
            scrollersRef.current = picked.all
            primaryScrollerRef.current.addEventListener("scroll", onScroll, { passive: true })

            scrollMomentumRef.current = {
                prevLeft: primaryScrollerRef.current.scrollLeft,
                netInWindow: 0,
            }

            if (!options?.skipCooling) {
                setCooling()
            }
        }

        attach()
        if (!primaryScrollerRef.current) {
            attachTimerRef.current = window.setTimeout(() => {
                attach()
                attachTimerRef.current = null
            }, 0)

            return () => {
                if (attachTimerRef.current) {
                    window.clearTimeout(attachTimerRef.current)
                    attachTimerRef.current = null
                }
                detach()
                if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current)
                observerRef.current?.disconnect()
                clearCoolingTimer()
            }
        }

        observerRef.current = new MutationObserver(() => {
            const picked = pickTimelineScrollers()
            const nextPrimary = picked?.primary ?? null
            const currentPrimary = primaryScrollerRef.current

            if (
                !currentPrimary ||
                !rootEl.contains(currentPrimary) ||
                (nextPrimary && nextPrimary !== currentPrimary)
            ) {
                attach()
            }
        })
        observerRef.current.observe(rootEl, { childList: true, subtree: true })

        return () => {
            detach()
            if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current)
            observerRef.current?.disconnect()
            if (attachTimerRef.current) {
                window.clearTimeout(attachTimerRef.current)
                attachTimerRef.current = null
            }
            clearCoolingTimer()
        }
    }, [
        calendarRef,
        containerRef,
        isEnabled,
        opts?.thresholdPx,
        opts?.debounceMs,
        opts?.cooldownMs,
        opts?.shiftByDays,
        opts?.edgeBufferHours,
    ])
}
